import { useState, useEffect } from 'react';
import { useLoader } from '../../../contexts/LoaderContext';
import { useAlert } from '../../../contexts/AlertContext';
import { AddUserFormValues } from '../types';
import { users, User, UpdateUserData } from '../../../api/users';
import { defaultValues } from '../types';
import { unformatPhoneNumber, formatPhoneNumber } from '../../../utils/phoneMask';

interface UseEditUserFormProps {
  userId: number;
  onSubmit: (values: AddUserFormValues) => void;
  onCancel: () => void;
}

export const useEditUserForm = ({ userId, onSubmit, onCancel }: UseEditUserFormProps) => {
  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = useAlert();
  const [userDetails, setUserDetails] = useState<User | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        showLoader();
        const response = await users.getById(userId);
        setUserDetails(response);
        hideLoader();
      } catch (error: any) {
        hideLoader();
        showAlert(error.message || 'Failed to fetch user details', 'error');
      }
    };

    fetchUser();
  }, [userId, showLoader, hideLoader, showAlert]);

  const handleSubmit = async (values: AddUserFormValues) => {
    try {
      showLoader();

      const userData: UpdateUserData = {
        firstName: values.firstName,
        lastName: values.lastName,
        agencyIds: values.agency.map((agency) => parseInt(agency)),
        role: values.role,
        hasMarketingHubAccess: values.accessTo.includes('marketing_hub'),
        hasLeaderboardAccess: values.accessTo.includes('leaderboard'),
        ...(values.accessTo.includes('leaderboard') && { phone: unformatPhoneNumber(values.phone) }),
        ...(values.role !== 'DIRECTOR' &&
          values.accessTo.includes('leaderboard') &&
          values.office?.length && {
          leaderboardOfficeIds: values.office.map((id) => parseInt(id))
        }),
      };

      await users.update(userId, userData);
      showAlert('User successfully updated', 'success');
      hideLoader();
      onSubmit(values);
    } catch (error: any) {
      hideLoader();
      if (
        error.message?.toLowerCase().includes('email') ||
        error.message?.toLowerCase().includes('duplicate')
      ) {
        showAlert(
          'This email is already in use. Please enter a different email address or check existing users.',
          'error'
        );
      } else {
        showAlert(error.message || 'Failed to save user', 'error');
      }
    }
  };

  const getInitialValues = (): AddUserFormValues => {
    if (userDetails) {
      const accessTo: string[] = [];
      if (userDetails.hasMarketingHubAccess) accessTo.push('marketing_hub');
      if (userDetails.hasLeaderboardAccess) accessTo.push('leaderboard');

      return {
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        email: userDetails.email,
        phone: userDetails.phone ? formatPhoneNumber(userDetails.phone) : '',
        agency: userDetails.agencies.map((agency) => agency.id.toString()),
        role: userDetails.role,
        accessTo,
        office: userDetails.leaderboardOffices?.map((o) => o.id.toString()) || [],
      };
    }
    return defaultValues;
  };

  return {
    handleSubmit,
    onCancel,
    getInitialValues,
    userDetails,
  };
};
